import React, { useEffect, useState } from 'react';

import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Footer from './components/Footer/index';
import Checkout from './steps/Checkout';
import CheckLease from './steps/CheckLease';
import CheckoutAon from './steps/CheckoutAon';
import VerificationCodeSubmission from './steps/VerificationCodeSubmission';
import LeaseAgreement from './steps/LeaseAgreement';
import BlockedVerificationCode from './steps/BlockedVerificationCode';
import PriceTagDisclosures from './steps/PriceTagDisclosures';
import PayCheckDetails from './steps/PayCheckDetails';
import Congratulation from './steps/Congratulation';
import NewUserFlow from './steps/NewUserFlow';
import NewUserIncome from './steps/NewUserIncome';
import NewUserReview from './steps/NewUserReview';
import ReturnUserFlow from './steps/ReturnUserFlow';
import Error from './steps/Error';
import { DisplayOfferedLeaseDetails } from './steps/DisplayOfferedLeaseDetails';
import WarrantyAgreement from './steps/WarrantyAgreement';
import NewUserDetails from './steps/NewUserDetails';
import NewUserApproved from './steps/NewUserApproved';
import ApprovedWithCart from './steps/ApprovedWithCart';
import NotApproved from './steps/NotApproved';
import AfterPayment from './steps/AfterPayment';
import SomethingWentWrong from './steps/SomethingWentWrong';
import InstoreVccError from './steps/InstoreVccError';
import ExceededLimit from './steps/ExceededLimit';
import VerifyIncome from './steps/VerifyIncome';
import DuePayment from './steps/DuePayment';
import ExceededLimitLCR from './steps/ExceededLimitLCR';
import AddressCheck from './steps/AddressCheck';
import CobrandHeader from './components/CobrandHeader';
import KatapultPayCard from './steps/KatapultPayCard';
import OrderCompleted from './steps/OrderCompleted';
import Breadcrumbs from './components/Breadcrumbs';
import RoundBreadcrumbs from './components/RoundBreadcrumbs';
import BackButton from './components/BackButton';
import CloseButton from './components/CloseButton';
import CartBackButton from './components/CartBackButton';
import Sidebar from './components/Sidebar';
import SidebarCheckout from './components/SidebarCheckout';
import Progressbar from './components/Progressbar';
import {
  initParameters,
  setSynchronyFlow,
  setShopifyFlow,
  setOfferPageFlow,
  setInstoreVccFlow,
  addPayFrequencyLookup,
  addVerificationCheckoutUid,
} from './redux/actions';
import isInIframe from './helpers/isInIframe';
import useInstoreVccInit from './hooks/useInstoreVccInit';
import fullStoryIdentify from './helpers/fullStoryIdentify';

import './assets/css/index.css';
import './assets/css/katapult-pay.css';
import 'animate.css/animate.min.css';

// eslint-disable-next-line no-shadow
function Katapult({ json, window }) {
  const [isReady, setIsReady] = useState(false);
  const [isShopifyFlow, setIsShopifyFlow] = useState(false);
  const [isOfferPageFlow, setIsOfferPageFlow] = useState(false);
  const [instoreVccStoreId, setInstoreVccStoreId] = useState(null);
  const [cancelUrl, setCancelUrl] = useState('');
  const [userData, setUserData] = useState('');
  const dispatch = useDispatch();
  const { failed: instoreVccFailed } = useInstoreVccInit(instoreVccStoreId, true);

  const store = useSelector(state => ({
    featureGateLinearFlow: state.loanInfo?.featureGateLinearFlow,
    payfrequencyExists: state.loanInfo?.payfrequencyExists,
    retailerName: state.technical?.retailerName,
    retailerId: state.technical?.retailerId,
    availableLimit: state.technical?.availableLimit,
    synchrony: state.technical?.synchrony,
    checkout: state?.loanInfo?.checkout,
    checkoutLookupData: state.personal?.checkoutLookupData,
    integrationType: state.technical?.initialize.integrationType,
    firstName: state.personal?.firstName,
    isInstoreVccFlow: state.technical?.instoreVccFlow?.isInstoreVccFlow,
    instoreVccLogoUrl: state.technical?.instoreVccFlow?.logoUrl,
    instoreVccStoreId: state.technical?.instoreVccFlow?.storeId,
    instoreVccDba: state.technical?.instoreVccFlow?.dba,
    checkoutUid: state.loanInfo?.checkoutUid,
    phone: state.contact?.phone,
  }));

  const handleDataFromChild = newData => {
    setCancelUrl(newData?.urls?.cancel);
    setUserData(newData?.customer?.billing);
  };

  // The in-store flow takes the phone on /new-user-flow and never reaches the identify call on the verification
  // screen, so the phone identity is set here as soon as the phone is known. That flow has no customer uid or first
  // name when the phone is entered, so the phone stands in for the uid and the retailer name for the first name.
  useEffect(() => {
    if (store.isInstoreVccFlow && store.phone) {
      fullStoryIdentify({ phone: store.phone, uid: store.phone, firstName: store.instoreVccDba || store.retailerName });
    }
  }, [store.isInstoreVccFlow, store.phone, store.instoreVccDba, store.retailerName]);

  useEffect(() => {
    dispatch(initParameters(json));
    setIsReady(true);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (window.location.search?.indexOf('synchrony') !== -1) {
      const appUid = url.searchParams.get('app_uid');
      const merchantToken = url.searchParams.get('merchant_token');
      dispatch(
        setSynchronyFlow({
          isSynchrony: true,
          merchantToken,
          appUid,
        }),
      );
      localStorage.setItem('synchronyRetailerToken', merchantToken);
    }
    if (url.searchParams.get('flow') === 'shopify') {
      const transactionId = url.searchParams.get('transaction_id');
      setIsShopifyFlow(true);
      dispatch(
        setShopifyFlow({
          isShopify: true,
          transactionId,
        }),
      );
    }

    if (url.searchParams.get('flow') === 'instore_vcc') {
      setInstoreVccStoreId(url.searchParams.get('store_id') || '');
      dispatch(setInstoreVccFlow({ isInstoreVccFlow: true }));
    }
    if (url.searchParams.get('flow') === 'offerpage') {
      setIsOfferPageFlow(true);
      const afterPaymentPageValue = url.searchParams.get('after_payment_page') === 'true';
      dispatch(
        setOfferPageFlow({
          isOfferPageFlow: true,
          uid: url.searchParams.get('uid'),
          token: url.searchParams.get('token'),
          afterPaymentPage: afterPaymentPageValue,
        }),
      );

      dispatch(addPayFrequencyLookup({ payfrequency_exists: true }));
      dispatch(addVerificationCheckoutUid(url.searchParams.get('uid')));
    }
  }, []);

  useEffect(() => {
    // setItem(undefined) stores the truthy string "undefined", which sendEvent.js would send as the value
    // and which would overwrite what the in-store location lookup saved.
    const toStore = {
      retailerName: store.retailerName,
      retailerId: store.retailerId,
      availableLimit: store.availableLimit,
    };
    Object.entries(toStore).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        localStorage.setItem(key, value);
      }
    });
  }, [store.retailerName]);

  if (!isInIframe(window)) {
    if (window && window.location && window.location.pathname && window.location.pathname === '/') {
      // eslint-disable-next-line no-console
      console.log('No Data');
    }
  }

  if (!isReady) {
    return null;
  }

  const BreadcrumbsWithRoute = withRouter(Breadcrumbs);
  const RoundBreadcrumbsWithRoute = withRouter(RoundBreadcrumbs);
  const BackButtonWithRoute = withRouter(BackButton);
  return (
    <Router>
      <div
        className={`k-wrapper ${isShopifyFlow && 'k-wrapper-shopify'} ${store.isInstoreVccFlow && 'k-wrapper-instore'}`}
      >
        {instoreVccFailed && <Redirect to="/error" />}
        {store.isInstoreVccFlow && (
          <>
            <CobrandHeader
              retailerName={store.instoreVccDba || store.retailerName}
              retailerLogoUrl={store.instoreVccLogoUrl}
            />
            <Progressbar />
            {/* Shopify skips the new-user Income step entirely, so the stepper always excludes
                it and shows just Offer -> Pricing -> Agreement -> Payment. */}
            <RoundBreadcrumbsWithRoute excludeFirst />
            <CartBackButton />
          </>
        )}
        {!isShopifyFlow && !store.isInstoreVccFlow && (
          <Switch>
            <Route>
              {!isOfferPageFlow && (
                <header className="k-header">
                  <BackButtonWithRoute />
                </header>
              )}
              <nav>
                <div className="k-logo">
                  <img alt="" src="./logo.svg" />
                </div>
                {store.featureGateLinearFlow && !store.synchrony?.isSynchrony && (
                  <RoundBreadcrumbsWithRoute excludeFirst={store.payfrequencyExists} />
                )}
                {!store.featureGateLinearFlow && !store.synchrony?.isSynchrony && (
                  <BreadcrumbsWithRoute excludeFirst={store.payfrequencyExists} />
                )}
              </nav>
            </Route>
          </Switch>
        )}
        {isShopifyFlow && (
          <>
            <nav className="k-shopify-header">
              <div className="k-logo">
                <img alt="" src="./logo.svg" />
              </div>
              <CloseButton url={cancelUrl} userData={userData} />
            </nav>
            <Progressbar />
            {/* Shopify skips the new-user Income step entirely, so the stepper always excludes
                it and shows just Offer -> Pricing -> Agreement -> Payment. */}
            <RoundBreadcrumbsWithRoute excludeFirst />
          </>
        )}
        <main className="k-main">
          <Switch>
            {store.isInstoreVccFlow && (
              <Route path={['/error']}>
                <InstoreVccError storeId={instoreVccStoreId || store.instoreVccStoreId} />
              </Route>
            )}
            <Route path="/lease-agreement">
              <LeaseAgreement />
            </Route>
            <Route path="/price-tag-disclosures">
              <PriceTagDisclosures />
            </Route>
            <Route path="/display-offered-lease-details">
              <DisplayOfferedLeaseDetails />
            </Route>
            <Route path="/pay-check-details">
              <PayCheckDetails />
            </Route>
            <Route path="/warranty-agreement">
              <WarrantyAgreement />
            </Route>
            <Route path="/checkout-aon">
              <CheckoutAon />
            </Route>
            <Route path="/checkout">
              <Checkout />
            </Route>
            <Route path="/blocked-verification-code">
              <BlockedVerificationCode />
            </Route>
            <Route path="/support">
              <Error />
            </Route>
            <Route path="/check-lease">
              <CheckLease />
            </Route>
            {/* Building the cart, the Katapult Pay card and order confirmation are only part of the
                in-store VCC flow. */}
            {store.isInstoreVccFlow && (
              <Route path="/approved-with-cart">
                <ApprovedWithCart />
              </Route>
            )}
            {store.isInstoreVccFlow && (
              <Route path="/katapult-pay-card">
                <KatapultPayCard />
              </Route>
            )}
            <Route path="/order-completed">
              {store.isInstoreVccFlow && store.checkoutUid ? <OrderCompleted /> : <Redirect to="/error" />}
            </Route>
            <Route path="/error">
              <SomethingWentWrong url={cancelUrl} />
            </Route>
            <Route path="/congratulation">
              <Congratulation />
            </Route>
            <Route path="/verify-income">
              <VerifyIncome />
            </Route>
            <Route path="/new-user-approved">
              <NewUserApproved />
            </Route>
            <Route path="/not-approved">
              <NotApproved url={cancelUrl} />
            </Route>
            <Route path="/due-payment">
              <DuePayment />
            </Route>
            <Route path="/new-user-details">
              <NewUserDetails />
            </Route>
            <Route path="/new-user-review">
              <NewUserReview />
            </Route>
            <Route path="/exceeded-limit">
              <ExceededLimit url={cancelUrl} />
            </Route>
            <Route path="/exceeded-limit-lcr">
              <ExceededLimitLCR url={cancelUrl} />
            </Route>
            <Route path="/address-check">
              <AddressCheck url={cancelUrl} />
            </Route>
            <Route path="/return-user-flow">
              <ReturnUserFlow />
            </Route>
            <Route path="/new-user-income">
              <NewUserIncome />
            </Route>
            <Route path="/new-user-flow">
              <NewUserFlow onDataChange={handleDataFromChild} />
            </Route>
            <Route path="/after-payment">
              <AfterPayment />
            </Route>
            <Route path="/">
              <VerificationCodeSubmission />
            </Route>
          </Switch>
        </main>
        <Footer />
      </div>
      <div className="k-sidebar-wrapper">
        <Sidebar />
        <SidebarCheckout data={store.checkout} checkout={store.checkoutLookupData} firstname={store.firstName} />
      </div>
    </Router>
  );
}

export default Katapult;
